import { signupMarketingContext } from "../lib/marketingTracking";
import { doc, getDoc } from "firebase/firestore";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
  User as FirebaseUser,
} from "firebase/auth";
import posthog from "posthog-js";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isValidPrivateCode } from "../lib/privateCodes";
import { setWorkspaceRequestContext } from "../lib/workspaceFetch";
import {
  normalizeWorkspaceSession,
  WorkspaceSession,
} from "../lib/workspaceSession";
import {
  inviteTokenFromSignInLocation,
  PENDING_INVITE_TOKEN_KEY,
  RECENTLY_ACCEPTED_INVITE_TOKEN_KEY,
} from "../lib/workspaceInviteIntent";
import { Customer } from "../models/User";
import { WorkspaceSummary } from "../models/Workspace";
import { auth, db } from "../services/Firebase";

interface AuthContextType {
  currentUser: FirebaseUser | null;
  customerData: Customer | null;
  workspaces: WorkspaceSummary[];
  activeWorkspaceId: string | null;
  activeWorkspace: WorkspaceSummary | null;
  loading: boolean;
  workspaceSwitching: boolean;
  workspaceFeaturesEnabled: boolean;
  workspaceBootstrapError: string | null;
  firebasePending: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  createCustomerIfNeeded: (user: User, privateCode?: string) => Promise<void>;
  refreshCustomerData: () => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  createWorkspace: (name: string) => Promise<void>;
  retryWorkspaceBootstrap: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const ACTIVE_WORKSPACE_KEY = "wavegen.activeWorkspaceId";
const analyticsEnabled = process.env.REACT_APP_POSTHOG_DISABLED !== "true";

async function retryableFetch(
  request: () => Promise<Response>,
): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await request();
      if (response.status < 500 || attempt === 2) return response;
    } catch (error) {
      lastError = error;
      if (attempt === 2) throw error;
    }
    await new Promise((resolve) =>
      window.setTimeout(resolve, attempt === 0 ? 250 : 750),
    );
  }
  throw lastError || new Error("Could not reach WaveGen.");
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [workspaceSwitching, setWorkspaceSwitching] = useState(false);
  const [workspaceFeaturesEnabled, setWorkspaceFeaturesEnabled] =
    useState(false);
  const [workspaceBootstrapError, setWorkspaceBootstrapError] = useState<
    string | null
  >(null);
  const [firebasePending, setFirebasePending] = useState(true);
  const ensureCustomerInFlight = useRef<
    Record<string, Promise<WorkspaceSession>>
  >({});
  const hadUser = useRef(false);
  const currentUserRef = useRef<FirebaseUser | null>(null);
  const authEpochRef = useRef(0);
  const workspaceIntentRef = useRef(0);
  const createWorkspaceInFlightRef = useRef<Promise<void> | null>(null);

  const apiBase = process.env.REACT_APP_API_BASE || "https://api.wavegen.ai";

  const applySession = useCallback((session: WorkspaceSession) => {
    // Cached pre-workspace servers do not allow x-workspace-id in CORS.
    // Only install the automatic header after the server explicitly confirms
    // that this identity is on the workspace-aware contract.
    setWorkspaceRequestContext(
      session.workspaceContextEnabled === true
        ? session.activeWorkspaceId || null
        : null,
    );
    setWorkspaces(session.workspaces || []);
    setActiveWorkspaceId(session.activeWorkspaceId || null);
    setCustomerData(session.customer || null);
    setWorkspaceFeaturesEnabled(session.workspaceFeaturesEnabled === true);
    setWorkspaceBootstrapError(null);
    if (session.activeWorkspaceId) {
      window.localStorage.setItem(
        ACTIVE_WORKSPACE_KEY,
        session.activeWorkspaceId,
      );
    } else {
      window.localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const previousUid = currentUserRef.current?.uid;
      authEpochRef.current += 1;
      workspaceIntentRef.current += 1;
      currentUserRef.current = user;
      ensureCustomerInFlight.current = {};
      createWorkspaceInFlightRef.current = null;
      setWorkspaceSwitching(false);
      setCustomerData(null);
      setWorkspaces([]);
      setWorkspaceRequestContext(null);
      setActiveWorkspaceId(null);
      setWorkspaceFeaturesEnabled(false);
      setWorkspaceBootstrapError(null);
      if (previousUid && previousUid !== user?.uid) {
        window.localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
      }
      if (user) {
        hadUser.current = true;
        if (analyticsEnabled) posthog.identify(user.uid, { email: user.email });
        setLoading(true);
      } else {
        if (hadUser.current && analyticsEnabled) posthog.reset();
        hadUser.current = false;
        setLoading(false);
      }
      setCurrentUser(user);
      setFirebasePending(false);
    });
    return () => unsubscribe();
  }, []);

  const ensureCustomerDocument = useCallback(
    async (
      user: FirebaseUser,
      privateCode?: string,
    ): Promise<WorkspaceSession> => {
      let validatedPrivateCode =
        privateCode && isValidPrivateCode(privateCode)
          ? privateCode
          : undefined;
      if (!validatedPrivateCode) {
        const pending = window.sessionStorage.getItem("pendingPrivateCode");
        if (pending && isValidPrivateCode(pending))
          validatedPrivateCode = pending;
      }

      const existingRequest = ensureCustomerInFlight.current[user.uid];
      if (existingRequest) return existingRequest;
      const requestAuthEpoch = authEpochRef.current;

      const request = (async () => {
        const token = await user.getIdToken();
        let inviteToken = window.sessionStorage.getItem(
          PENDING_INVITE_TOKEN_KEY,
        );
        if (!inviteToken) {
          inviteToken = inviteTokenFromSignInLocation(window.location);
          if (inviteToken) {
            // Email sign-in links commonly open in a new tab, where
            // sessionStorage from the invitation tab is unavailable. Restore
            // the fragment token before auth state triggers account bootstrap
            // so the new user joins before any personal workspace is created.
            window.sessionStorage.setItem(
              PENDING_INVITE_TOKEN_KEY,
              inviteToken,
            );
          }
        }
        const preferredWorkspaceId =
          window.localStorage.getItem(ACTIVE_WORKSPACE_KEY);
        const openAccount = (pendingInviteToken?: string) =>
          retryableFetch(() =>
            fetch(`${apiBase}/api/customers/ensure`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                ...(process.env.REACT_APP_META_TRACKING_ENABLED === "true"
                  ? { marketing: signupMarketingContext() }
                  : {}),
                ...(validatedPrivateCode
                  ? { privateCode: validatedPrivateCode }
                  : {}),
                ...(pendingInviteToken
                  ? { inviteToken: pendingInviteToken }
                  : {}),
                ...(preferredWorkspaceId
                  ? { activeWorkspaceId: preferredWorkspaceId }
                  : {}),
              }),
            }),
          );

        let response = await openAccount(inviteToken || undefined);
        let acceptedPendingInvite = Boolean(inviteToken && response.ok);

        // An invite belongs to one exact email address. A stale link or a link
        // opened in the wrong signed-in account must not make that person's
        // existing WaveGen workspaces inaccessible.
        if (
          inviteToken &&
          !response.ok &&
          [403, 404, 410].includes(response.status)
        ) {
          if (response.status === 404 || response.status === 410) {
            window.sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
            window.sessionStorage.removeItem(
              RECENTLY_ACCEPTED_INVITE_TOKEN_KEY,
            );
          }
          response = await openAccount();
          acceptedPendingInvite = false;
        }
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(
            body.error || `Failed to open account (${response.status})`,
          );
        }
        const session = normalizeWorkspaceSession(await response.json(), user);
        if (
          authEpochRef.current !== requestAuthEpoch ||
          currentUserRef.current?.uid !== user.uid
        ) {
          throw Object.assign(new Error("Stale account bootstrap ignored."), {
            name: "AbortError",
          });
        }
        applySession(session);
        setLoading(false);
        if (validatedPrivateCode) {
          window.sessionStorage.removeItem("pendingPrivateCode");
        }
        if (acceptedPendingInvite && inviteToken) {
          // Keep one short-lived handoff token for InvitePage. Otherwise a
          // successful automatic acceptance can render as "not found" after
          // bootstrap removes the only session-scoped copy.
          window.sessionStorage.setItem(
            RECENTLY_ACCEPTED_INVITE_TOKEN_KEY,
            inviteToken,
          );
          window.sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
        }
        return session;
      })();

      ensureCustomerInFlight.current[user.uid] = request;
      try {
        return await request;
      } finally {
        if (ensureCustomerInFlight.current[user.uid] === request) {
          delete ensureCustomerInFlight.current[user.uid];
        }
      }
    },
    [apiBase, applySession],
  );

  useEffect(() => {
    if (firebasePending || !currentUser) return;
    let canceled = false;
    ensureCustomerDocument(currentUser).catch((error) => {
      if (canceled) return;
      if ((error as Error)?.name === "AbortError") return;
      console.error("Error opening WaveGen account:", error);
      setWorkspaceBootstrapError(
        error instanceof Error
          ? error.message
          : "WaveGen could not load your workspace.",
      );
      setLoading(false);
    });
    return () => {
      canceled = true;
    };
  }, [currentUser, firebasePending, ensureCustomerDocument]);

  const refreshWorkspaces = useCallback(async () => {
    if (!currentUser) return;
    const requestAuthEpoch = authEpochRef.current;
    const requestWorkspaceIntent = workspaceIntentRef.current;
    const requestUid = currentUser.uid;
    const token = await currentUser.getIdToken();
    const preferred = activeWorkspaceId
      ? `?activeWorkspaceId=${encodeURIComponent(activeWorkspaceId)}`
      : "";
    const response = await fetch(`${apiBase}/api/workspaces${preferred}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 404 && activeWorkspaceId) {
      // Client-first rollout compatibility with the pre-workspace server.
      const snapshot = await getDoc(
        doc(
          db,
          process.env.REACT_APP_CUSTOMERS_DB ?? "customers_test",
          activeWorkspaceId,
        ),
      );
      if (
        authEpochRef.current !== requestAuthEpoch ||
        workspaceIntentRef.current !== requestWorkspaceIntent ||
        currentUserRef.current?.uid !== requestUid
      ) {
        return;
      }
      const customer = snapshot.exists() ? (snapshot.data() as Customer) : null;
      setCustomerData(customer);
      // The checklist reads the workspace summary, including after remounts.
      // Keep that summary in sync when an older server needs the ledger fallback.
      if (customer) {
        setWorkspaces((previous) =>
          previous.map((workspace) =>
            workspace.id === activeWorkspaceId
              ? {
                  ...workspace,
                  setupPillDismissed: customer.metadata?.setupPillDismissed === true,
                }
              : workspace,
          ),
        );
      }
      return;
    }
    if (!response.ok) throw new Error("Failed to refresh workspaces.");
    const session = normalizeWorkspaceSession(
      await response.json(),
      currentUser,
    );
    if (
      authEpochRef.current !== requestAuthEpoch ||
      workspaceIntentRef.current !== requestWorkspaceIntent ||
      currentUserRef.current?.uid !== requestUid
    ) {
      return;
    }
    applySession(session);
  }, [currentUser, activeWorkspaceId, apiBase, applySession]);

  useEffect(() => {
    if (!currentUser) return;
    const refresh = () =>
      void refreshWorkspaces().catch((error) =>
        console.error("Failed to refresh workspace access:", error),
      );
    const interval = window.setInterval(refresh, 60_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("wavegen:workspace-access-lost", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("wavegen:workspace-access-lost", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [currentUser, refreshWorkspaces]);

  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      if (!currentUser || workspaceId === activeWorkspaceId) return;
      const requestAuthEpoch = authEpochRef.current;
      const requestUid = currentUser.uid;
      const requestWorkspaceIntent = ++workspaceIntentRef.current;
      setWorkspaceSwitching(true);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${apiBase}/api/workspaces/${encodeURIComponent(workspaceId)}/activate`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "x-workspace-id": workspaceId,
            },
          },
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Failed to switch workspace.");
        }
        const session = normalizeWorkspaceSession(
          await response.json(),
          currentUser,
        );
        if (
          authEpochRef.current !== requestAuthEpoch ||
          workspaceIntentRef.current !== requestWorkspaceIntent ||
          currentUserRef.current?.uid !== requestUid
        ) {
          return;
        }
        applySession(session);
      } finally {
        if (workspaceIntentRef.current === requestWorkspaceIntent) {
          setWorkspaceSwitching(false);
        }
      }
    },
    [currentUser, activeWorkspaceId, apiBase, applySession],
  );

  const createWorkspace = useCallback(
    (name: string): Promise<void> => {
      if (!currentUser) throw new Error("Sign in to create a workspace.");
      if (createWorkspaceInFlightRef.current) {
        return createWorkspaceInFlightRef.current;
      }
      const requestAuthEpoch = authEpochRef.current;
      const requestUid = currentUser.uid;
      const requestWorkspaceIntent = ++workspaceIntentRef.current;
      const requestId = window.crypto.randomUUID();
      setWorkspaceSwitching(true);
      const operation = (async () => {
        try {
          const token = await currentUser.getIdToken();
          const response = await retryableFetch(() =>
            fetch(`${apiBase}/api/workspaces`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ name, requestId }),
            }),
          );
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || "Failed to create workspace.");
          }
          const session = normalizeWorkspaceSession(
            await response.json(),
            currentUser,
          );
          if (
            authEpochRef.current !== requestAuthEpoch ||
            workspaceIntentRef.current !== requestWorkspaceIntent ||
            currentUserRef.current?.uid !== requestUid
          ) {
            return;
          }
          applySession(session);
        } finally {
          if (workspaceIntentRef.current === requestWorkspaceIntent) {
            setWorkspaceSwitching(false);
          }
        }
      })();
      createWorkspaceInFlightRef.current = operation;
      void operation.then(
        () => {
          if (createWorkspaceInFlightRef.current === operation) {
            createWorkspaceInFlightRef.current = null;
          }
        },
        () => {
          if (createWorkspaceInFlightRef.current === operation) {
            createWorkspaceInFlightRef.current = null;
          }
        },
      );
      return operation;
    },
    [currentUser, apiBase, applySession],
  );

  const createCustomerIfNeeded = async (user: User, privateCode?: string) => {
    if (privateCode && isValidPrivateCode(privateCode)) {
      window.sessionStorage.setItem("pendingPrivateCode", privateCode);
    }
    await ensureCustomerDocument(user, privateCode);
  };

  const refreshCustomerData = refreshWorkspaces;
  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };
  const signInWithGoogle = async () => {
    await signInWithPopup(auth, new GoogleAuthProvider());
  };
  const logout = async () => {
    authEpochRef.current += 1;
    workspaceIntentRef.current += 1;
    currentUserRef.current = null;
    ensureCustomerInFlight.current = {};
    setWorkspaceRequestContext(null);
    setCustomerData(null);
    setWorkspaces([]);
    setActiveWorkspaceId(null);
    setWorkspaceFeaturesEnabled(false);
    window.localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    await signOut(auth);
  };

  const retryWorkspaceBootstrap = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setWorkspaceBootstrapError(null);
    try {
      await ensureCustomerDocument(currentUser);
    } catch (error) {
      setWorkspaceBootstrapError(
        error instanceof Error
          ? error.message
          : "WaveGen could not load your workspace.",
      );
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentUser, ensureCustomerDocument]);

  const activeWorkspace = useMemo(
    () =>
      workspaces.find((workspace) => workspace.id === activeWorkspaceId) ||
      null,
    [workspaces, activeWorkspaceId],
  );

  const value: AuthContextType = {
    currentUser,
    customerData,
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    loading,
    workspaceSwitching,
    workspaceFeaturesEnabled,
    workspaceBootstrapError,
    firebasePending,
    signInWithEmail,
    signInWithGoogle,
    logout,
    createCustomerIfNeeded,
    refreshCustomerData,
    refreshWorkspaces,
    switchWorkspace,
    createWorkspace,
    retryWorkspaceBootstrap,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
